import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { ReportingPerson } from '@gokrew/models/reporting';
import { ComboBox, type ComboBoxOption } from '@gokrew/ui';
import { useDebounce } from '@/hooks/useDebounce';
import { useManagerOptions } from '@/hooks/reporting/useReporting';
import { personName } from '@/utils/reporting';
import styles from './ManagerPicker.module.css';

interface ManagerPickerProps {
  /**
   * The employee being edited; never offered as their own manager. Absent
   * for a new hire who is not saved yet.
   */
  employeeId?: string;
  value: ReportingPerson | null;
  onChange: (person: ReportingPerson | null) => void;
  disabled?: boolean;
  /**
   * Offer "No reporting manager" as a choice. Off where a person is required
   * (a handover successor or interim).
   */
  allowNone?: boolean;
  /** Defaults to "Choose a manager". */
  placeholder?: string;
  /** Other people never offered (e.g. the person leaving). */
  excludeIds?: string[];
}

/**
 * Picks a reporting manager among the company's active employees, in any
 * branch (a manager may sit in another branch). Searches server-side and only
 * once the picker is opened.
 */
export const ManagerPicker = memo(
  ({
    employeeId,
    value,
    onChange,
    disabled,
    allowNone = true,
    placeholder,
    excludeIds,
  }: ManagerPickerProps) => {
    const { t } = useTranslation(TranslationNS.Employee);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const debounced = useDebounce(search.trim(), 300);
    const { data: people = [], isFetching } = useManagerOptions(
      debounced,
      employeeId,
      open && !disabled
    );
    // Until the search settles, the list says "Loading", not "No results".
    const isSearching = isFetching || debounced !== search.trim();

    const byId = useMemo(() => {
      const excluded = new Set(excludeIds);
      const map = new Map(
        people
          .filter((p) => !excluded.has(p.employeeId))
          .map((p) => [p.employeeId, p])
      );
      if (value) map.set(value.employeeId, value);
      return map;
    }, [people, value, excludeIds]);

    const options = useMemo<ComboBoxOption[]>(
      () =>
        [...byId.values()].map((person) => ({
          value: person.employeeId,
          label: personName(person),
          description:
            [person.designation, person.branchName]
              .filter(Boolean)
              .join(' · ') || undefined,
        })),
      [byId]
    );

    return (
      <div className={styles.picker}>
        <ComboBox
          options={options}
          value={value?.employeeId ?? ''}
          onChange={(id) => onChange(id ? (byId.get(id) ?? null) : null)}
          onSearch={setSearch}
          onOpenChange={setOpen}
          disabled={disabled}
          none={allowNone}
          noneLabel={t('reporting_line.no_manager')}
          placeholder={placeholder ?? t('reporting_line.pick_manager')}
          searchPlaceholder={t('reporting_line.search_manager')}
          emptyMessage={isSearching ? t('reporting_line.loading') : undefined}
          noEntriesMessage={
            isSearching ? t('reporting_line.loading') : undefined
          }
          popoverClassName={styles.popover}
        />
      </div>
    );
  }
);

ManagerPicker.displayName = 'ManagerPicker';
